import type {
  AttributesEntity,
  ValuesEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductOrder, ProductOrderBy } from '@eliadmin/core/src/utils/product-utils';

export type CategoryFilterState = {
  subCategories: number[];
  manufacturers: number[];
  suppliers: number[];
  suppliers_pins: {
    id: number;
    adress: string;
    slug: string;
    name: string;
    latitude: number;
    longitude: number;
  }[];
  supliers_distance: number;
  availabilities: number[];
  statuses: number[];
  attributes: { id: number; values: number[] }[];
  sort: { order: ProductOrder; orderBy: ProductOrderBy };
  // Zero means no explicit price constraint, not the category's current bound.
  price_min: number;
  price_max: number;
  discounted: boolean;
};

export type CategoryFilterUpdate = {
  subCategory?: number;
  manufacturer?: number;
  supplier?: number | number[];
  suppliers_pins?: CategoryFilterState['suppliers_pins'];
  suppliers_distance?: number;
  availability?: number;
  availabilities?: number[];
  status?: number;
  selectedValue?: { attribute: AttributesEntity; value: ValuesEntity };
  sort?: CategoryFilterState['sort'];
  price_min?: number;
  price_max?: number;
  discounted?: boolean;
  replace?: boolean;
  attributes?: CategoryFilterState['attributes'];
  remove?: boolean;
};

export type CategoryUrlState = { filter: CategoryFilterState; page: number };

export function createDefaultCategoryFilter(): CategoryFilterState {
  return {
    subCategories: [],
    manufacturers: [],
    suppliers: [],
    suppliers_pins: [],
    supliers_distance: 0,
    availabilities: [],
    statuses: [],
    attributes: [],
    sort: { order: null, orderBy: null },
    price_min: 0,
    price_max: 0,
    discounted: false,
  };
}

function positiveIds(values: string[]) {
  return [...new Set(values.map(Number).filter(value => Number.isSafeInteger(value) && value > 0))];
}

export function normalizeCategoryPage(page: unknown) {
  const value = Number(page);
  return Number.isSafeInteger(value) && value > 0 ? value : 1;
}

export function parseCategoryFilterState(searchParams: URLSearchParams): CategoryUrlState {
  const filter = createDefaultCategoryFilter();
  filter.subCategories = positiveIds(searchParams.getAll('pCategory'));
  filter.manufacturers = positiveIds(searchParams.getAll('manufacturer'));
  filter.suppliers = positiveIds(searchParams.getAll('supplier'));
  filter.availabilities = positiveIds(searchParams.getAll('availability'));
  filter.statuses = positiveIds(searchParams.getAll('status'));
  filter.discounted = searchParams.get('discounted') === 'true';

  const order = searchParams.get('order');
  const orderBy = searchParams.get('orderBy');
  filter.sort.order = order === 'asc' || order === 'desc' ? order : null;
  const allowedOrderBy = ['id', 'price', 'sells', 'position', 'views', 'tags_created_at'];
  filter.sort.orderBy = allowedOrderBy.includes(orderBy ?? '') ? (orderBy as ProductOrderBy) : null;

  const min = Number(searchParams.get('price_min'));
  const max = Number(searchParams.get('price_max'));
  filter.price_min = Number.isFinite(min) && min > 0 ? min : 0;
  filter.price_max = Number.isFinite(max) && max > filter.price_min ? max : 0;

  for (const raw of searchParams.getAll('attribute')) {
    const parts = raw.split(':');
    if (parts.length !== 2) continue;
    const [id, value] = parts.map(Number);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(value) || value <= 0) continue;
    const existing = filter.attributes.find(attribute => attribute.id === id);
    if (!existing) filter.attributes.push({ id, values: [value] });
    else if (!existing.values.includes(value)) existing.values.push(value);
  }

  return { filter, page: normalizeCategoryPage(searchParams.get('page')) };
}

/** Keep unrelated URL state (tracking, search, etc.) while replacing category filters. */
export function writeCategoryFilterState(
  searchParams: URLSearchParams,
  { filter, page }: CategoryUrlState,
) {
  const next = new URLSearchParams(searchParams);
  const repeated: [string, number[]][] = [
    ['pCategory', filter.subCategories],
    ['manufacturer', filter.manufacturers],
    ['supplier', filter.suppliers],
    ['availability', filter.availabilities],
    ['status', filter.statuses],
  ];
  for (const [key, values] of repeated) {
    next.delete(key);
    for (const value of new Set(values)) next.append(key, String(value));
  }
  next.delete('attribute');
  const seen = new Set<string>();
  for (const attribute of filter.attributes) {
    for (const value of attribute.values) {
      const pair = `${attribute.id}:${value}`;
      if (seen.has(pair)) continue;
      next.append('attribute', pair);
      seen.add(pair);
    }
  }
  const optional: [string, string | null][] = [
    ['order', filter.sort.order],
    ['orderBy', filter.sort.orderBy],
    ['price_min', filter.price_min > 0 ? String(filter.price_min) : null],
    ['price_max', filter.price_max > filter.price_min ? String(filter.price_max) : null],
    ['discounted', filter.discounted ? 'true' : null],
    ['page', normalizeCategoryPage(page) > 1 ? String(page) : null],
  ];
  for (const [key, value] of optional) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  return next;
}

function toggle(values: number[], value: number) {
  return values.includes(value) ? values.filter(item => item !== value) : [...values, value];
}

/** Preserve the core setFilter contract, but apply a user interaction as one transaction. */
export function applyCategoryFilterChange(
  current: CategoryFilterState,
  update: CategoryFilterUpdate,
): CategoryFilterState {
  const next = { ...current };
  if (update.subCategory) next.subCategories = toggle(current.subCategories, update.subCategory);
  if (update.manufacturer) next.manufacturers = toggle(current.manufacturers, update.manufacturer);
  if (update.supplier !== undefined) {
    next.suppliers = Array.isArray(update.supplier)
      ? [...update.supplier]
      : toggle(current.suppliers, update.supplier);
  }
  if (update.suppliers_pins) next.suppliers_pins = [...update.suppliers_pins];
  if (update.suppliers_distance !== undefined) next.supliers_distance = update.suppliers_distance;
  if (update.availability) next.availabilities = toggle(current.availabilities, update.availability);
  if (update.availabilities) next.availabilities = [...update.availabilities];
  if (update.status) next.statuses = toggle(current.statuses, update.status);
  if (update.selectedValue) {
    const { attribute, value } = update.selectedValue;
    const existing = current.attributes.find(item => item.id === attribute.id);
    next.attributes = current.attributes.filter(item => item.id !== attribute.id);
    if (!update.remove) {
      const values = existing?.values.includes(value.id)
        ? existing.values.filter(id => id !== value.id)
        : update.replace || !existing
          ? [value.id]
          : [...existing.values, value.id];
      if (values.length) next.attributes.push({ id: attribute.id, values });
    }
  }
  if (update.attributes) {
    next.attributes = update.attributes
      .filter(attribute => attribute.values.length > 0)
      .map(attribute => ({ id: attribute.id, values: [...new Set(attribute.values)] }));
  }
  if (update.sort) next.sort = { ...update.sort };
  if (typeof update.price_min === 'number') next.price_min = update.price_min;
  if (typeof update.price_max === 'number') next.price_max = update.price_max;
  if (typeof update.discounted === 'boolean') next.discounted = update.discounted;
  return next;
}
